import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { SzamlazzhuService } from 'src/app/services/szamlazzhu/szamlazzhu.service';
import {
  GetKsziInfoResponse,
  SzamlazzhuClientItem,
} from 'src/app/services/szamlazzhu/types';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';

@Component({
  selector: 'app-invoice-resend',
  templateUrl: './invoice-resend.component.html',
  styleUrls: ['./invoice-resend.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    PayeeButtonComponent,
  ],
})
export class InvoiceResendComponent implements OnInit, OnDestroy {
  private szamlazzhuService = inject(SzamlazzhuService);
  private fb = inject(FormBuilder);
  private snackbar = inject(MatSnackBar);

  private readonly destroy = new Subject<void>();

  readonly formGroup = this.fb.group({
    client: this.fb.control<SzamlazzhuClientItem | null>(
      null,
      Validators.required,
    ),
    invoiceNumber: this.fb.control<string | null>(null, Validators.required),
  });

  loading = false;

  clientSearchSubject = new Subject<string>();
  clients: SzamlazzhuClientItem[] = [];

  get client() {
    return this.formGroup.controls.client;
  }
  get invoiceNumber() {
    return this.formGroup.controls.invoiceNumber;
  }

  ksziInfo?: GetKsziInfoResponse;
  ksziInterval?: NodeJS.Timeout;

  ngOnInit(): void {
    this.clientSearchSubject
      .pipe(takeUntil(this.destroy), debounceTime(1000))
      .subscribe({
        next: async (search) => await this.setClients(search),
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();

    clearInterval(this.ksziInterval);
  }

  async submit() {
    if (this.formGroup.invalid || this.loading) {
      return;
    }

    const { client, invoiceNumber } = this.formGroup.getRawValue();
    if (!client || !invoiceNumber) {
      return;
    }

    try {
      this.loading = true;
      await this.szamlazzhuService.resendInvoice(client.uuid, invoiceNumber);

      this.snackbar.open('Számla újraküldése folyamatban!', 'OK', {
        duration: 5000,
      });

      this.ksziInfo = await this.szamlazzhuService.getKsziInfo(client.uuid);
      clearInterval(this.ksziInterval);
      this.ksziInterval = setInterval(async () => {
        this.ksziInfo = await this.szamlazzhuService.getKsziInfo(client.uuid);
      }, 10_000);
    } catch (error) {
      console.error('Error while resending invoice', error);
      this.snackbar.open('Valami hiba történt!', 'OK', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  }

  clientDisplayWith(item?: SzamlazzhuClientItem): string {
    return item?.name ?? '';
  }

  private async setClients(search: string): Promise<void> {
    const result = await this.szamlazzhuService.getSzamlazzhuClients({
      search,
    });
    this.clients = result.clients;
  }
}
